import * as L from 'leaflet';
import polyline from './poliyline';

// Extend the Leaflet namespace to include Routing
declare module 'leaflet' {
  namespace Routing {
    class Google extends Class {
      options: any;
      directionsService: any;
      constructor(options?: any);
      initialize(options: any): void;
      _flatten(arrs: any[]): any[];
      _decodePolyline(geometry: string): L.LatLng[];
      route(waypoints: any[], callback: Function, context?: any, options?: any): void;
    }
    function google(options?: any): Google;
  }
}

// Initialize the Routing namespace if it doesn't exist - método más seguro
const LeafletWithRouting = L as any;

// Verificar si el objeto es extensible antes de intentar modificarlo
try {
  if (!LeafletWithRouting.Routing) {
    // Intentar de manera segura
    if (Object.isExtensible(LeafletWithRouting)) {
      LeafletWithRouting.Routing = {};
    } else {
      // Si no es extensible, crear una copia temporal
      console.warn('Leaflet object is not extensible, using alternative approach');
      // Fallback: usar una definición local
      (window as any).L_Routing_Temp = {};
    }
  }
} catch (error) {
  console.warn('Error initializing Leaflet.Routing:', error);
  // Fallback: definir en window si Leaflet no es extensible
  (window as any).L_Routing_Temp = {};
}

// Usar el namespace apropiado
const RoutingNamespace = LeafletWithRouting.Routing || (window as any).L_Routing_Temp || {};

// Crear la clase Google con tipo correcto
const GoogleRoutingClass = L.Class.extend({
  options: {},
  initialize: function (options) {
    this.options = L.extend(this.options, {
      travelMode: google.maps.TravelMode.DRIVING,
      unitSystem: google.maps.UnitSystem.METRIC,
      provideRouteAlternatives: true
    });
    this.directionsService = new google.maps.DirectionsService();
    L.Util.setOptions(this, options);
  },
  _flatten: function (arrs) {
    var arr = [],
      i;
    for (i = 0; i < arrs.length; i++) arr = arr.concat(arrs[i]);
    return arr;
  },
  _decodePolyline: function (geometry: string) {
    var coords = (polyline as any).decode(geometry, 5),
      latlngs = new Array(coords.length),
      i;
    for (i = 0; i < coords.length; i++) {
      latlngs[i] = new (L as any).LatLng(coords[i][0], coords[i][1]);
    }

    return latlngs;
  },
  _maneuverToInstructionType: function (maneuver) {
    switch (maneuver) {
      case 'turn-right':
        return 'Right';
      case 'turn-slight-right':
      case 'ramp-right':
      case 'fork-right':
        return 'SlightRight';
      case 'turn-sharp-right':
        return 'SharpRight';
      case 'turn-left':
        return 'Left';
      case 'turn-slight-left':
      case 'ramp-left':
      case 'fork-left':
        return 'SlightLeft';
      case 'turn-sharp-left':
        return 'SharpLeft';
      case 'uturn-right':
      case 'uturn-left':
        return 'TurnAround';
      case 'roundabout-left':
      case 'roundabout-right':
        return 'Roundabout';
      default:
        return 'Straight';
    }
  },
  route: function (waypoints: any[], callback: Function, context?: any, options?: any) {
    var that = this;
    var directions = L.extend({}, this.options);
    if (options && options.geometryOnly) {
      directions.provideRouteAlternatives = false;
    }
    directions.origin = waypoints[0].latLng.lat + ',' + waypoints[0].latLng.lng;
    directions.destination =
      waypoints[waypoints.length - 1].latLng.lat + ',' + waypoints[waypoints.length - 1].latLng.lng;
    directions.waypoints = waypoints.slice(1, waypoints.length - 1).map(function (waypoint) {
      return {
        location: waypoint.latLng.lat + ',' + waypoint.latLng.lng,
        stopover: false
      };
    });

    this.directionsService.route(directions, function (result: any, status: any) {
      if (status == google.maps.DirectionsStatus.OK) {
        var r = result.routes.map(function (route: any) {
          var iroute: any = {},
            i: number,
            j: number,
            step: any;
          iroute.name = route.summary;
          iroute.summary = {
            totalDistance: that
              ._flatten(
                route.legs.map(function (leg) {
                  return leg.steps.map(function (step) {
                    return step.distance.value;
                  });
                })
              )
              .reduce(function (previousValue, currentValue) {
                return previousValue + currentValue;
              }),
            totalTime: that
              ._flatten(
                route.legs.map(function (leg) {
                  return leg.steps.map(function (step) {
                    return step.duration.value;
                  });
                })
              )
              .reduce(function (previousValue, currentValue) {
                return previousValue + currentValue;
              })
          };

          iroute.coordinates = [];
          var indices = [],
            indicesSecondary = [];

          var waypointIndices = [0];
          var nWaypoint = 1;

          for (i = 0; i < route.legs.length; i++) {
            indicesSecondary = [];
            var stepsPoints = [];

            for (j = 0; j < route.legs[i].steps.length; j++) {
              step = route.legs[i].steps[j];
              var points = that._decodePolyline(step.polyline.points);
              stepsPoints[j] = points.length;
              indicesSecondary.push(iroute.coordinates.length);
              iroute.coordinates = iroute.coordinates.concat(points);
            }
            indices.push(indicesSecondary);

            for (var k = 0; k < route.legs[i].via_waypoints.length; k++) {
              var si = route.legs[i].via_waypoint[k].step_index;
              var sp = route.legs[i].via_waypoint[k].step_interpolation;

              waypointIndices[nWaypoint] = indicesSecondary[si] + Math.floor(stepsPoints[si] * sp);
              nWaypoint++;
            }
          }

          iroute.inputWaypoints = waypoints || [];
          iroute.waypoints = iroute.actualWaypoints = waypoints;

          waypointIndices[nWaypoint] = iroute.coordinates.length - 1;
          iroute.waypointIndices = waypointIndices;

          if (!options || !options.geometryOnly) {
            iroute.instructions = [];
            for (i = 0; i < route.legs.length; i++)
              for (j = 0; j < route.legs[i].steps.length; j++) {
                step = route.legs[i].steps[j];
                iroute.instructions.push({
                  type: that._maneuverToInstructionType(step.maneuver),
                  text: step.instructions.replace(/<(?:.|\n)*?>/gm, ''),
                  distance: step.distance.value,
                  time: step.duration.value,
                  index: indices[i][j],
                  exit: null //instr.exit_number
                });
              }
          }
          return iroute;
        });
        try {
          callback.call(context || callback, null, r);
        } catch {}
      } else {
        try {
          callback.call(context, {
            status: status,
            message: result
          });
        } catch {}
      }
    });
    return this;
  }
});

// Asignar la clase de manera segura
try {
  if (RoutingNamespace === LeafletWithRouting.Routing) {
    // Caso normal: Leaflet.Routing es extensible
    RoutingNamespace.Google = GoogleRoutingClass;
    RoutingNamespace.google = function (options?: any) {
      return new (RoutingNamespace.Google as any)(options);
    };
  } else {
    // Caso fallback: usar window
    RoutingNamespace.Google = GoogleRoutingClass;
    RoutingNamespace.google = function (options?: any) {
      return new (RoutingNamespace.Google as any)(options);
    };

    // También exponer globalmente para acceso directo
    (window as any).LeafletRoutingGoogle = GoogleRoutingClass;
    (window as any).leafletRoutingGoogle = function (options?: any) {
      return new (GoogleRoutingClass as any)(options);
    };
  }
} catch (error) {
  console.error('Error setting up Leaflet Routing Google:', error);
  // Fallback final: solo exponer globalmente
  (window as any).LeafletRoutingGoogle = GoogleRoutingClass;
  (window as any).leafletRoutingGoogle = function (options?: any) {
    return new (GoogleRoutingClass as any)(options);
  };
}
